import { GetTenantDTO } from '../../models/getTenantDTO';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { UserDTO } from 'app/core/api/models/userDTO';
import { GetNameOfEmail } from '@shared/util/emailToName';
import { catchError, Observable, retry, shareReplay, throwError } from 'rxjs';
import { CreateUserDTO } from '../../models/createUserDTO';
import { DepartmentFilter } from '../../models/departmentFilter';
import { Log } from '../../models/log';
import { OrganizationFilter } from '../../models/organizationFilter';
import { Tenant } from '../../models/tenant';
import { SearchUserRequestDTO } from '@shared/models/searchUserRequestDTO';
import { ApplicationSecret } from '../../models/applicationSecret';
import { CreatedApplication } from '../../models/createdApplication';
import { TenantDTO } from '../../models/tenantDTO';
import { CreateTenantDTO } from '../../models/createTenanatDTO';
import { CostQueryRequestDto, CostQueryResponseDto } from '../../../../core/api/models/costDashboardQueryDTO.model';
import {
  AzureSubscriptionDto,
  CreateAzureSubscriptionDto,
  AzureProductSummaryDto,
  CustomerDeploymentDto,
  CreateCustomerDeploymentDto,
  UpdateCustomerDeploymentDto,
  DeploymentDefaultAssignmentDto,
  ApiKeyDeploymentAssignmentDto,
  ConfiguredDeploymentModelDto,
  CreateConfiguredDeploymentModelDto,
  UpdateConfiguredDeploymentModelDto,
  DeploymentModelActivationState,
  ResponsesCapabilitiesDto,
  ResponsesCapabilitiesBackfillRequestDto,
  ResolutionPreviewDto,
  ResolutionPreviewRequestDto,
  WritableResponsesCapabilitiesDto,
} from 'app/core/api/models/costExplorer.model';

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  private baseUrl = environment.baseUrl;

  private microsoftGraphUserEndpoint = environment.microsoftGraphUserEndpoint;

  private authenticationMethodResetPath = environment.authenticationMethodResetPath;

  private tenantController = 'management/tenants';

  private userController = 'management/users';

  private suggestionController = 'management/users/search';

  private tenantList$: Observable<CreateTenantDTO[]>;

  private adminTenantsList$: Observable<Tenant[]>;

  constructor(private http: HttpClient) {}

  getAdminTenants(): Observable<Tenant[]> {
    if (!this.adminTenantsList$) {
      this.adminTenantsList$ = this.http
        .get<Tenant[]>(this.baseUrl + this.tenantController + '/admin')
        .pipe(retry(3), catchError(this.formatErrors))
        .pipe(shareReplay(1));
    }
    return this.adminTenantsList$;
  }

  addUserToDepartment(departmentId: string, user: string, token: string): Observable<UserDTO> {
    var name = GetNameOfEmail.getNameOfEmail(user);

    var userDTO = {
      email: user,
      name: name,
      departmentId: departmentId,
    } as CreateUserDTO;

    let body = {
      Token: token,
      CreateUserRequestDTO: userDTO,
    };

    return this.http.post<UserDTO>(this.baseUrl + this.userController, body);
  }

  private formatErrors(error: any): Observable<never> {
    return throwError(() => new Error(error.error));
  }

  deleteTenant(tenantId: string): Observable<boolean> {
    return this.http.delete<boolean>(this.baseUrl + this.tenantController + '/' + tenantId);
  }

  getNameOfUser(departmentId: string, userId: string): Observable<string> {
    return this.http.get<string>(
      this.baseUrl + this.userController + '/department/' + departmentId + '/user/' + userId + '/name',
    );
  }

  getLogsOfDepartmentWithFilter(filter: DepartmentFilter): Observable<Log[]> {
    return this.http.put<Log[]>(this.baseUrl + 'management/logs/departments/filter', filter);
  }

  getLogsByOrganizationId(organizationId: string): Observable<Log[]> {
    return this.http.get<Log[]>(this.baseUrl + 'management/logs/organizations/' + organizationId);
  }

  getLogsOfOrganizationWithFilter(filter: OrganizationFilter): Observable<Log[]> {
    return this.http.put<Log[]>(this.baseUrl + 'management/logs/organizations/filter', filter);
  }

  getTenant(tenantId: string): Observable<GetTenantDTO> {
    return this.http.get<GetTenantDTO>(this.baseUrl + this.tenantController + '/' + tenantId);
  }

  confirmOnboarding(onboardingId: string, customerEmail: string): Observable<boolean> {
    const body = {
      OnboardingId: onboardingId,
      CustomerEmail: customerEmail,
    };
    return this.http.put<boolean>(this.baseUrl + 'management/onboarding/confirm', body);
  }

  finishOnboarding(body: any): Observable<boolean> {
    return this.http.post<boolean>(this.baseUrl + 'management/onboarding/finish', body);
  }

  createApplication(redirectUri: string): Observable<CreatedApplication> {
    return this.http.post<CreatedApplication>('https://graph.microsoft.com/v1.0/applications', {
      displayName: 'TokenControl-Automated-Service-Principal',
      requiredResourceAccess: [
        {
          resourceAppId: '00000003-0000-0000-c000-000000000000',
          resourceAccess: [
            {
              id: 'e1fe6dd8-ba31-4d61-89e7-88639da4683d',
              type: 'Scope',
            },
            {
              id: 'a154be20-db9c-4678-8ab7-66f6cc099a59',
              type: 'Scope',
            },
            {
              id: '06da0dbc-49e2-44d2-8312-53f166ab848a',
              type: 'Scope',
            },
            {
              id: 'df021288-bdef-4463-88db-98f22de89214',
              type: 'Role',
            },
            {
              id: '7ab1d382-f21e-4acd-a863-ba3e13f7da61',
              type: 'Role',
            },
            {
              id: '06b708a9-e830-4db3-a914-8e69da51d44f',
              type: 'Role',
            },
            {
              id: '19dbc75e-c2e2-444c-a770-ec69d8559fc7', // ID für Directory.ReadWrite.All
              type: 'Role', // Typ 'Role' für Anwendungsberechtigungen
            },
            {
              id: '9e3f62cf-ca93-4989-b6ce-bf83c28f9fe8',
              type: 'Role',
            },
            {
              id: 'd01b97e9-cbc0-49fe-810a-750afd5527a3',
              type: 'Scope',
            },
          ],
        },
      ],
      spa: {
        redirectUris: [redirectUri],
      },
    });
  }

  getApplication(appId: string): Observable<any> {
    return this.http.get<any>(
      'https://graph.microsoft.com/v1.0/applications/' + appId + '?$select=id,appId,displayName,requiredResourceAccess',
    );
  }

  addSecretToServicePrincipal(appId: string): Observable<ApplicationSecret> {
    return this.http.post<ApplicationSecret>(
      'https://graph.microsoft.com/v1.0/applications/' + appId + '/addPassword',
      {
        passwordCredential: {
          displayName: 'TokenControl-ServicePrincipal-Secret',
        },
      },
    );
  }

  deleteApplication(appId: string): Observable<any> {
    return this.http.delete<any>('https://graph.microsoft.com/v1.0/applications/' + appId);
  }

  deleteServicePrincipal(spId: string): Observable<any> {
    return this.http.delete<any>('https://graph.microsoft.com/v1.0/servicePrincipals/' + spId);
  }

  getUserSuggestions(
    searchString: string,
    tenantsToInclude: Array<TenantDTO> = new Array<TenantDTO>(),
  ): Observable<Array<UserDTO>> {
    const body = {
      searchTerm: searchString,
      tenantsToInclude: tenantsToInclude,
    } as SearchUserRequestDTO;
    return this.http.post<Array<UserDTO>>(this.baseUrl + this.suggestionController, body);
  }

  getGroupSuggestions(searchString: string, tenantsToInclude: TenantDTO[] = []): Observable<any[]> {
    const body = { searchTerm: searchString, tenantsToInclude: tenantsToInclude };
    return this.http.post<any[]>(this.baseUrl + 'management/groups/search', body);
  }

  updateUser(user: UserDTO): Observable<UserDTO> {
    return this.http.put<UserDTO>(this.baseUrl + this.userController, user);
  }

  queryPrivateGptCost(costQueryRequestDto: CostQueryRequestDto): Observable<CostQueryResponseDto> {
    return this.http.post<CostQueryResponseDto>(this.baseUrl + 'management/privategpt/cost', costQueryRequestDto);
  }

  getApiHealthStatus(): Observable<any> {
    return this.http.get<any>(this.baseUrl + 'health');
  }

  /**
   * Gets all Azure subscriptions from the Cost API
   */
  getAzureSubscriptions(): Observable<AzureSubscriptionDto[]> {
    return this.http.get<AzureSubscriptionDto[]>(this.baseUrl + 'api/cost/subscriptions');
  }

  /**
   * Gets a single Azure subscription from the Cost API
   */
  getAzureSubscriptionById(subscriptionId: string): Observable<AzureSubscriptionDto> {
    return this.http.get<AzureSubscriptionDto>(
      this.baseUrl + 'api/cost/subscriptions/' + encodeURIComponent(subscriptionId),
    );
  }

  /**
   * Creates a new Azure subscription in the Cost API
   */
  createAzureSubscription(dto: CreateAzureSubscriptionDto): Observable<AzureSubscriptionDto> {
    return this.http.post<AzureSubscriptionDto>(this.baseUrl + 'api/cost/subscriptions', dto);
  }

  /**
   * Deletes an Azure subscription from the Cost API
   */
  deleteAzureSubscription(subscriptionId: string): Observable<void> {
    return this.http.delete<void>(this.baseUrl + 'api/cost/subscriptions/' + encodeURIComponent(subscriptionId));
  }

  /**
   * Gets distinct products from cost details for a subscription.
   * Useful for discovering available ProductIds when creating model mappings.
   */
  getSubscriptionProducts(
    subscriptionId: string,
    serviceFamily: string = 'AI + Machine Learning',
  ): Observable<AzureProductSummaryDto[]> {
    const params = serviceFamily ? { serviceFamily } : {};
    return this.http.get<AzureProductSummaryDto[]>(
      this.baseUrl + 'api/cost/subscriptions/' + encodeURIComponent(subscriptionId) + '/products',
      { params },
    );
  }

  /**
   * Gets customer deployments for an organization/department cloudControlId.
   */
  getCustomerDeployments(
    cloudControlId: string,
    includeInherited: boolean = false,
  ): Observable<CustomerDeploymentDto[]> {
    let params = new HttpParams();
    if (includeInherited) {
      params = params.set('includeInherited', 'true');
    }

    return this.http.get<CustomerDeploymentDto[]>(
      this.baseUrl + 'api/cost/consumption/' + encodeURIComponent(cloudControlId) + '/deployments',
      { params },
    );
  }

  previewResolution(cloudControlId: string, dto: ResolutionPreviewRequestDto): Observable<ResolutionPreviewDto> {
    return this.http.post<ResolutionPreviewDto>(
      this.baseUrl + 'api/cost/consumption/' + encodeURIComponent(cloudControlId) + '/resolution/preview',
      dto,
    );
  }

  /**
   * Creates a customer deployment for an organization/department cloudControlId.
   */
  createCustomerDeployment(
    cloudControlId: string,
    dto: CreateCustomerDeploymentDto,
  ): Observable<CustomerDeploymentDto> {
    return this.http.post<CustomerDeploymentDto>(
      this.baseUrl + 'api/cost/consumption/' + encodeURIComponent(cloudControlId) + '/deployments',
      dto,
    );
  }

  /**
   * Updates an existing customer deployment for an organization/department cloudControlId.
   */
  updateCustomerDeployment(
    cloudControlId: string,
    deploymentKey: string,
    dto: UpdateCustomerDeploymentDto,
  ): Observable<CustomerDeploymentDto> {
    return this.http.put<CustomerDeploymentDto>(
      this.baseUrl +
        'api/cost/consumption/' +
        encodeURIComponent(cloudControlId) +
        '/deployments/' +
        encodeURIComponent(deploymentKey),
      dto,
    );
  }

  /**
   * Deletes a customer deployment for an organization/department cloudControlId.
   */
  deleteCustomerDeployment(cloudControlId: string, deploymentKey: string): Observable<void> {
    return this.http.delete<void>(
      this.baseUrl +
        'api/cost/consumption/' +
        encodeURIComponent(cloudControlId) +
        '/deployments/' +
        encodeURIComponent(deploymentKey),
    );
  }

  /**
   * Assigns the default deployment for an organization/department cloudControlId.
   */
  setDefaultDeployment(cloudControlId: string, deploymentKey: string): Observable<DeploymentDefaultAssignmentDto> {
    return this.http.put<DeploymentDefaultAssignmentDto>(
      this.baseUrl + 'api/cost/consumption/' + encodeURIComponent(cloudControlId) + '/deployments/default',
      { deploymentKey },
    );
  }

  /**
   * Updates the active/inactive status of a deployment for an organization/department cloudControlId.
   */
  setDeploymentStatus(
    cloudControlId: string,
    deploymentKey: string,
    isActive: boolean,
  ): Observable<CustomerDeploymentDto> {
    return this.http.put<CustomerDeploymentDto>(
      this.baseUrl +
        'api/cost/consumption/' +
        encodeURIComponent(cloudControlId) +
        '/deployments/' +
        encodeURIComponent(deploymentKey) +
        '/status',
      { isActive },
    );
  }

  /**
   * Removes the default deployment assignment for an organization/department cloudControlId.
   */
  clearDefaultDeployment(cloudControlId: string): Observable<void> {
    return this.http.delete<void>(
      this.baseUrl + 'api/cost/consumption/' + encodeURIComponent(cloudControlId) + '/deployments/default',
    );
  }

  getDefaultResponsesCapabilities(cloudControlId: string): Observable<ResponsesCapabilitiesDto> {
    return this.http.get<ResponsesCapabilitiesDto>(
      this.baseUrl +
        'api/cost/consumption/' +
        encodeURIComponent(cloudControlId) +
        '/deployments/responses-capabilities/default',
    );
  }

  backfillResponsesCapabilities(
    cloudControlId: string,
    dto: ResponsesCapabilitiesBackfillRequestDto,
  ): Observable<void> {
    return this.http.post<void>(
      this.baseUrl +
        'api/cost/consumption/' +
        encodeURIComponent(cloudControlId) +
        '/deployments/responses-capabilities/backfill',
      dto,
    );
  }

  updateDeploymentResponsesCapabilities(
    cloudControlId: string,
    deploymentKey: string,
    dto: WritableResponsesCapabilitiesDto,
  ): Observable<void> {
    return this.http.put<void>(
      this.baseUrl +
        'api/cost/consumption/' +
        encodeURIComponent(cloudControlId) +
        '/deployments/' +
        encodeURIComponent(deploymentKey) +
        '/responses-capabilities',
      dto,
    );
  }

  clearDeploymentResponsesCapabilities(cloudControlId: string, deploymentKey: string): Observable<void> {
    return this.http.delete<void>(
      this.baseUrl +
        'api/cost/consumption/' +
        encodeURIComponent(cloudControlId) +
        '/deployments/' +
        encodeURIComponent(deploymentKey) +
        '/responses-capabilities',
    );
  }

  updateDeploymentModelResponsesCapabilities(
    cloudControlId: string,
    deploymentKey: string,
    requestSelector: string,
    dto: WritableResponsesCapabilitiesDto,
  ): Observable<void> {
    return this.http.put<void>(
      this.baseUrl +
        'api/cost/consumption/' +
        encodeURIComponent(cloudControlId) +
        '/deployments/' +
        encodeURIComponent(deploymentKey) +
        '/models/' +
        encodeURIComponent(requestSelector) +
        '/responses-capabilities',
      dto,
    );
  }

  clearDeploymentModelResponsesCapabilities(
    cloudControlId: string,
    deploymentKey: string,
    requestSelector: string,
  ): Observable<void> {
    return this.http.delete<void>(
      this.baseUrl +
        'api/cost/consumption/' +
        encodeURIComponent(cloudControlId) +
        '/deployments/' +
        encodeURIComponent(deploymentKey) +
        '/models/' +
        encodeURIComponent(requestSelector) +
        '/responses-capabilities',
    );
  }

  /**
   * Gets the deployment assignment for an existing API key.
   */
  getApiKeyDeploymentAssignment(cloudControlId: string, keyId: string): Observable<ApiKeyDeploymentAssignmentDto> {
    return this.http.get<ApiKeyDeploymentAssignmentDto>(
      this.baseUrl +
        'api/cost/consumption/' +
        encodeURIComponent(cloudControlId) +
        '/keys/' +
        encodeURIComponent(keyId) +
        '/deployment',
    );
  }

  /**
   * Updates the deployment assignment for an existing API key.
   */
  setApiKeyDeploymentAssignment(
    cloudControlId: string,
    keyId: string,
    dto: ApiKeyDeploymentAssignmentDto,
  ): Observable<ApiKeyDeploymentAssignmentDto> {
    return this.http.put<ApiKeyDeploymentAssignmentDto>(
      this.baseUrl +
        'api/cost/consumption/' +
        encodeURIComponent(cloudControlId) +
        '/keys/' +
        encodeURIComponent(keyId) +
        '/deployment',
      dto,
    );
  }

  // ==================== Deployment Models ====================

  getDeploymentModels(
    cloudControlId: string,
    deploymentKey: string,
    includeInherited: boolean = true,
  ): Observable<ConfiguredDeploymentModelDto[]> {
    let params = new HttpParams();
    if (!includeInherited) {
      params = params.set('includeInherited', 'false');
    }
    return this.http.get<ConfiguredDeploymentModelDto[]>(
      this.baseUrl +
        'api/cost/consumption/' +
        encodeURIComponent(cloudControlId) +
        '/deployments/' +
        encodeURIComponent(deploymentKey) +
        '/models',
      { params },
    );
  }

  addDeploymentModel(
    cloudControlId: string,
    deploymentKey: string,
    dto: CreateConfiguredDeploymentModelDto,
  ): Observable<CustomerDeploymentDto> {
    return this.http.post<CustomerDeploymentDto>(
      this.baseUrl +
        'api/cost/consumption/' +
        encodeURIComponent(cloudControlId) +
        '/deployments/' +
        encodeURIComponent(deploymentKey) +
        '/models',
      dto,
    );
  }

  updateDeploymentModel(
    cloudControlId: string,
    deploymentKey: string,
    requestSelector: string,
    dto: UpdateConfiguredDeploymentModelDto,
  ): Observable<CustomerDeploymentDto> {
    return this.http.put<CustomerDeploymentDto>(
      this.baseUrl +
        'api/cost/consumption/' +
        encodeURIComponent(cloudControlId) +
        '/deployments/' +
        encodeURIComponent(deploymentKey) +
        '/models/' +
        encodeURIComponent(requestSelector),
      dto,
    );
  }

  deleteDeploymentModel(cloudControlId: string, deploymentKey: string, requestSelector: string): Observable<void> {
    return this.http.delete<void>(
      this.baseUrl +
        'api/cost/consumption/' +
        encodeURIComponent(cloudControlId) +
        '/deployments/' +
        encodeURIComponent(deploymentKey) +
        '/models/' +
        encodeURIComponent(requestSelector),
    );
  }

  toggleDeploymentModelActive(
    cloudControlId: string,
    deploymentKey: string,
    requestSelector: string,
    state: DeploymentModelActivationState,
  ): Observable<CustomerDeploymentDto> {
    return this.http.patch<CustomerDeploymentDto>(
      this.baseUrl +
        'api/cost/consumption/' +
        encodeURIComponent(cloudControlId) +
        '/deployments/' +
        encodeURIComponent(deploymentKey) +
        '/models/' +
        encodeURIComponent(requestSelector) +
        '/active',
      { state },
    );
  }

  /**
   * Creates a Service Principal for an existing application in the customer tenant
   * Based on: https://learn.microsoft.com/en-us/graph/api/serviceprincipal-post-serviceprincipals
   */
  createServicePrincipal(appId: string): Observable<any> {
    return this.http.post<any>('https://graph.microsoft.com/v1.0/servicePrincipals', {
      appId: appId,
    });
  }

  /**
   * Gets a Service Principal by appId
   */
  getServicePrincipal(appId: string): Observable<any> {
    return this.http.get<any>("https://graph.microsoft.com/v1.0/servicePrincipals?$filter=appId eq '" + appId + "'");
  }

  /**
   * Gets Service Principal by object ID
   */
  getServicePrincipalById(objectId: string): Observable<any> {
    return this.http.get<any>('https://graph.microsoft.com/v1.0/servicePrincipals/' + objectId);
  }

  /**
   * Resets user password using Microsoft Graph API
   */
  resetUserPassword(resetRequest: any): Observable<any> {
    return this.http.post<any>(this.baseUrl + 'management/groups/password-reset', resetRequest);
  }

  /**
   * Gets all groups of the current user
   */
  getGroupsOfUser(request: { token: string }): Observable<any> {
    return this.http.post<any>(this.baseUrl + 'management/groups', request);
  }

  /**
   * Gets a specific group with its members
   */
  getGroupByIdWithMembers(groupId: string, request: { token: string }): Observable<any> {
    return this.http.post<any>(this.baseUrl + 'management/groups/' + groupId, request);
  }
}
